import React, { Children, useState, useEffect } from "react";
// @ts-ignore
import { Helmet } from "react-helmet";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { ToastContainer } from "react-toastify";

import useNotification from "components/shared/helpers/use_notification";
import useCurrentTester from "components/shared/current_tester/use_current_tester";
import { testerAccountPaths } from "configuration/app_paths";
// @ts-ignore
import sentry from "app/sentry";
import BaseLayout from "components/layout/base";
// @ts-ignore
import Wootric from "app/wootric";
import Alerts from "components/shared/alerts";
import SideNavigation from "components/layout/shared/side_navigation";
import SplashPageLayout from "components/layout/content/splash";
import RedirectionsFilter from "components/utils/filters/redirections_filter";
import TopNavigation from "components/layout/shared/top_navigation";
import { useGetFeatureFlagsQuery } from "generated/graphql";
import globalActions from "actions/global";
import featureFlags from "app/feature_flags";
import { GlobalAlerts } from "types/app";

type TesterAccountLayoutProps = {
  globalActions: {
    removeAlert: (key: string) => void;
  };
  alerts: GlobalAlerts;
  children: React.ReactElement;
};

const TesterAccountLayout = ({ globalActions, children, alerts }: TesterAccountLayoutProps) => {
  const location = useLocation();
  const notify = useNotification();
  const { currentTester } = useCurrentTester();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery({
    fetchPolicy: "cache-and-network",
    pollInterval: featureFlags.pollInterval,
  });
  const [isSideBarCollapsed, setSideBarCollapsed] = useState(true);
  const toggleSideBar = () => setSideBarCollapsed(current => !current);

  useEffect(() => {
    const error = queryString.parse(location.search)?.error;

    if (error && typeof error === "string") notify(error, undefined, "danger");
  }, []);

  useEffect(() => {
    if (featureFlagsData) {
      featureFlags.setEnabled(featureFlagsData.featureFlags);
    }
  }, [featureFlagsData]);

  useEffect(() => {
    if (currentTester) {
      sentry.setUserContext(currentTester.publicId);
    }
  }, [currentTester]);

  if (!currentTester) return null;

  const splashPageLayout = [testerAccountPaths.checkUpVerification, testerAccountPaths.consent].includes(
    location.pathname,
  );

  const contentLayout = splashPageLayout ? <SplashPageLayout>{children}</SplashPageLayout> : Children.only(children);

  const wootric = <Wootric currentTester={currentTester} />;
  const alertsBox = <Alerts alerts={alerts} removeAlert={globalActions.removeAlert} />;

  const sidebar = (
    <SideNavigation
      currentTester={currentTester}
      isSideBarCollapsed={isSideBarCollapsed}
      toggleSideBar={toggleSideBar}
    />
  );
  const topNavigation = <TopNavigation toggleSideBar={toggleSideBar} />;

  const auxillaryContent = {
    headerContent: wootric,
    alerts: alertsBox,
    sidebarContent: sidebar,
    topNavigation,
  };

  const filledContentLayout = React.cloneElement(contentLayout, auxillaryContent);

  return (
    <BaseLayout testerAccount>
      <ToastContainer
        autoClose={5000}
        draggable={false}
        className="fixed bottom-0 mb-sm ml-lg"
        style={{ zIndex: 200 }}
      />
      <RedirectionsFilter currentTester={currentTester}>
        <Helmet title="Tester Account" />
        <div>{filledContentLayout}</div>
      </RedirectionsFilter>
    </BaseLayout>
  );
};

function mapStateToProps({ alerts }: { alerts: GlobalAlerts }) {
  return { alerts };
}

function mapDispatchToProps(dispatch: Dispatch<{ type: string }>) {
  return {
    globalActions: bindActionCreators(globalActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TesterAccountLayout);
